import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Componente para rastrear Web Vitals (Core Web Vitals)
 *
 * Rastreia métricas importantes de performance:
 * - LCP (Largest Contentful Paint): Tempo até o maior elemento visível ser renderizado
 * - FID (First Input Delay): Tempo entre primeira interação e resposta
 * - CLS (Cumulative Layout Shift): Mudanças inesperadas de layout
 * - FCP (First Contentful Paint): Tempo até o primeiro conteúdo ser renderizado
 * - TTFB (Time to First Byte): Tempo até receber o primeiro byte do servidor
 */
export function WebVitalsTracker() {
  useEffect(() => {
    // Verificar se o browser suporta Web Vitals
    if (typeof window === 'undefined') return;

    // Rastrear LCP (Largest Contentful Paint)
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };

      const value = lastEntry.renderTime || lastEntry.loadTime || 0;

      Sentry.setMeasurement('lcp', value, 'millisecond');

      // Adicionar como breadcrumb para contexto
      Sentry.addBreadcrumb({
        category: 'web-vitals',
        message: 'LCP (Largest Contentful Paint)',
        level: 'info',
        data: { value, unit: 'ms' },
      });
    });

    try {
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      // Browser não suporta largest-contentful-paint
    }

    // Rastrear FID (First Input Delay)
    const fidObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEntry & { processingStart?: number };
        const value = fidEntry.processingStart ? fidEntry.processingStart - entry.startTime : 0;

        Sentry.setMeasurement('fid', value, 'millisecond');

        Sentry.addBreadcrumb({
          category: 'web-vitals',
          message: 'FID (First Input Delay)',
          level: 'info',
          data: { value, unit: 'ms' },
        });
      });
    });

    try {
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // Browser não suporta first-input
    }

    // Rastrear CLS (Cumulative Layout Shift)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        const clsEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!clsEntry.hadRecentInput) {
          clsValue += clsEntry.value || 0;
        }
      });

      Sentry.setMeasurement('cls', clsValue, '');

      Sentry.addBreadcrumb({
        category: 'web-vitals',
        message: 'CLS (Cumulative Layout Shift)',
        level: 'info',
        data: { value: clsValue },
      });
    });

    try {
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      // Browser não suporta layout-shift
    }

    // Rastrear FCP (First Contentful Paint)
    const fcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        Sentry.setMeasurement('fcp', entry.startTime, 'millisecond');

        Sentry.addBreadcrumb({
          category: 'web-vitals',
          message: 'FCP (First Contentful Paint)',
          level: 'info',
          data: { value: entry.startTime, unit: 'ms' },
        });
      });
    });

    try {
      fcpObserver.observe({ type: 'paint', buffered: true });
    } catch (e) {
      // Browser não suporta paint
    }

    // Rastrear TTFB (Time to First Byte)
    if (window.performance && window.performance.timing) {
      const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navEntry) {
        const ttfb = navEntry.responseStart - navEntry.requestStart;

        Sentry.setMeasurement('ttfb', ttfb, 'millisecond');

        Sentry.addBreadcrumb({
          category: 'web-vitals',
          message: 'TTFB (Time to First Byte)',
          level: 'info',
          data: { value: ttfb, unit: 'ms' },
        });
      }
    }

    // Rastrear outras métricas de performance
    if (window.performance && window.performance.timing) {
      const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navEntry) {
        // DOM Content Loaded
        const domContentLoaded = navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart;
        Sentry.setMeasurement('dom_content_loaded', domContentLoaded, 'millisecond');

        // DOM Interactive
        const domInteractive = navEntry.domInteractive - navEntry.fetchStart;
        Sentry.setMeasurement('dom_interactive', domInteractive, 'millisecond');

        // Load Complete
        const loadComplete = navEntry.loadEventEnd - navEntry.fetchStart;
        Sentry.setMeasurement('load_complete', loadComplete, 'millisecond');

        Sentry.addBreadcrumb({
          category: 'performance',
          message: 'Performance metrics captured',
          level: 'info',
          data: {
            domContentLoaded,
            domInteractive,
            loadComplete,
            unit: 'ms',
          },
        });
      }
    }

    // Cleanup
    return () => {
      lcpObserver.disconnect();
      fidObserver.disconnect();
      clsObserver.disconnect();
      fcpObserver.disconnect();
    };
  }, []);

  return null;
}
